import React, { useRef, useState } from 'react'
import Slider from 'react-slick'

//* styles
import { SliderOuter } from '../../slider/style'
import { LogoQuoteInner, QuoteInner, QuoteOuter } from '../style'

//* components
import Button from '../../button'
import Typography from '../../typography'
import WrapperImage from '../../wrapperImage'

//* utils
import { QuoteImage } from '../../../../constants/svg'
import ImgUtil from '../../../../utils/img.utils'
import { renderSlides } from './slides'

const QuoteSlider = (props: any) => {
  const customSlider = useRef<any>()
  const logoQuoteSliderRef = useRef<any>([])
  const useCase = props.data.useCase
  const panels = props.data.panels || []

  const [slideNumber, setSlideNumber] = useState(0)
  const [activeLogoIndex, setActiveLogoIndex] = useState(0)
  const imageContainer = useRef<HTMLDivElement>()

  const sliderTheme = props.data.sliderTheme || props.data.theme || 'Light'

  const LOGO_QUOTE = 'Logo Quote'
  const MOBILE_LOGO_LIMIT = 4
  const DESKTOP_LOGO_LIMIT = 6

  const logoQuoteSlider = {
    arrows: false,
    infinite: panels.length <= DESKTOP_LOGO_LIMIT ? false : true,
    slidesToShow: DESKTOP_LOGO_LIMIT,
    slidesToScroll: 1,
    swipeToSlide: false,
    draggable: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          infinite: panels.length <= MOBILE_LOGO_LIMIT ? false : true,
          slidesToShow: MOBILE_LOGO_LIMIT,
          slidesToScroll: 1,
        },
      },
    ],
  }

  const settings = {
    dots: false,
    infinite: true,
    arrows: false,
    fade: useCase === LOGO_QUOTE,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (current: number, next: number) => {
      if (useCase === LOGO_QUOTE) setActiveLogoIndex(next)
      else {
        if (imageContainer && imageContainer.current && imageContainer.current.children) {
          imageContainer.current.children[slideNumber] &&
            imageContainer.current.children[slideNumber].classList.remove('Quote__Image--Active')

          imageContainer.current.children[slideNumber] &&
            imageContainer.current.children[next].classList.add('Quote__Image--Active')
        }
        setSlideNumber(next)
      }
    },
  }

  // ----- LOGO QUOTE SLIDER HANDLERS ----- //
  const handleLogoClick = (e: any) => {
    let index = e.target?.dataset?.index || activeLogoIndex

    let s = e.target.closest('.slick-slide')
    let prev = s?.previousElementSibling
    let next = s?.nextElementSibling

    //* if prev item is not active, we're at the left most item, move slider 1 to the left
    if (!prev?.classList?.contains('slick-active')) {
      logoQuoteSliderRef.current.slickPrev()
    }

    //* if next item is not active, we're at the right most item, move slider 1 to the right
    else if (!next?.classList?.contains('slick-active')) {
      logoQuoteSliderRef.current.slickNext()
    }

    customSlider.current.slickGoTo(index)
  }

  const homePageSliderPrevClick = () => {
    let active = document
      .querySelector(`.slick-active [data-index="${activeLogoIndex}"`)
      ?.closest('.slick-active')

    let previousSlibling = active?.previousElementSibling?.previousElementSibling

    let activeSlide =
      (+activeLogoIndex - 1 < 0 ? panels.length - 1 : +activeLogoIndex - 1) % panels.length
    customSlider.current.slickGoTo(activeSlide)

    if (previousSlibling && !previousSlibling.classList?.contains('slick-active')) {
      logoQuoteSliderRef.current.slickPrev()
    }
  }

  const homePageSliderNextClick = () => {
    let active = document
      .querySelector(`.slick-active [data-index="${activeLogoIndex}"`)
      ?.closest('.slick-active')

    let nextSibling = active?.nextElementSibling?.nextElementSibling

    let activeSlide = (+activeLogoIndex + 1) % panels.length
    customSlider.current.slickGoTo(activeSlide)

    if (nextSibling && !nextSibling.classList?.contains('slick-active')) {
      logoQuoteSliderRef.current.slickNext()
    }
  }

  const handleSlideNext = () => {
    if (useCase === LOGO_QUOTE) homePageSliderNextClick()
    else if (typeof customSlider !== undefined && customSlider.current) {
      customSlider.current.slickGoTo((slideNumber + 1) % panels.length)
    }
  }

  const handleSlidePrev = () => {
    if (useCase === LOGO_QUOTE) homePageSliderPrevClick()
    else if (typeof customSlider !== undefined && customSlider.current) {
      customSlider.current.slickGoTo(
        (slideNumber - 1 < 0 ? panels.length - 1 : slideNumber - 1) % panels.length
      )
    }
  }

  const renderImages = () => {
    const image = []
    if (panels.length) {
      for (let i = 0; i < panels.length; i++) {
        if (panels[i] && panels[i].image) {
          let altText = panels[i].image.altText ? panels[i].image.altText : ''
          let imageUrl =
            panels[i].image.image && panels[i].image.image.file && panels[i].image.image.file.url
              ? panels[i].image.image.file.url
              : ''

          if (imageUrl)
            image.push(
              <WrapperImage
                loading="lazy"
                {...(i === 0
                  ? { className: 'Quote__Image Quote__Image--Active' }
                  : { className: 'Quote__Image' })}
                src={ImgUtil.getResizedImageUrl(imageUrl, 'width=282')}
                alt={altText}
                key={`QuoteSlider-Image--${i}`}
                threshold={650}
              />
            )
        }
      }
    }

    return image
  }

  const prevButton = (
    <Button
      type="icon"
      clickHandler={handleSlidePrev}
      ariaLabel="Previous slide"
      arrowOrientation="left"
      theme={sliderTheme.toLowerCase()}
    />
  )

  const nextButton = (
    <Button
      type="icon"
      clickHandler={handleSlideNext}
      ariaLabel="Next slide"
      arrowOrientation="right"
      theme={sliderTheme.toLowerCase()}
    />
  )

  const quoteSlider = (
    <SliderOuter className="Quote__Slider-Outer">
      {/* @ts-ignore */}
      <Slider {...settings} ref={(slider: any) => (customSlider.current = slider)}>
        {renderSlides(panels, useCase, sliderTheme)}
      </Slider>
    </SliderOuter>
  )

  const QuoteSliderMarkup = (
    <QuoteOuter $quoteTheme={sliderTheme} data-cy="QuoteSlider">
      <QuoteInner className="Quote__Slider">
        <QuoteImage />
        <div className="Quote__Inner">
          {/* @ts-ignore   */}
          <div ref={imageContainer} className="Quote__Image-Container">
            {renderImages()}
          </div>

          <div className="Quote__Text">
            <div className="Quote__Slider-Nav-Container">
              {prevButton}

              <div className="Quote__Slider-Slide-Position">
                <Typography type="leadXS">
                  {slideNumber + 1} / {panels.length}
                </Typography>
              </div>

              {nextButton}
            </div>
            {quoteSlider}
          </div>
        </div>
      </QuoteInner>
    </QuoteOuter>
  )

  const LogoQuoteSliderMarkup = (
    <QuoteOuter $quoteTheme={sliderTheme} data-cy="LogoQuoteSlider">
      <LogoQuoteInner className="Quote__Slider">
        <div className="Quote__Inner">
          <div className="Quote__Text">
            <div className="Quote__Slider-Prev-Button-Container">{prevButton}</div>
            {quoteSlider}
            <div className="Quote__Slider-Next-Button-Container">{nextButton}</div>
          </div>

          <div className="Logo-Slider-Outer">
            {/* @ts-ignore */}
            <Slider {...logoQuoteSlider} className="Logo-Slider" ref={logoQuoteSliderRef}>
              {panels.map((panel: any, index: number) => {
                let isActive =
                  index == activeLogoIndex
                    ? {
                        className: 'Logo-Slider-Item--Active',
                      }
                    : {}

                return (
                  <div key={`slider_item-${index}`} className="Logo-Slider-Item">
                    <img
                      {...isActive}
                      onClick={handleLogoClick}
                      data-index={index}
                      src={panel?.companyLogo?.image?.file?.url}
                      alt={panel?.companyLogo?.altText}
                    />
                  </div>
                )
              })}
            </Slider>
          </div>
        </div>
      </LogoQuoteInner>
    </QuoteOuter>
  )

  return useCase === LOGO_QUOTE ? LogoQuoteSliderMarkup : QuoteSliderMarkup
}

export default QuoteSlider
